import { create } from "zustand";
import {
  type Experiment,
  type Rule,
  type Filter,
  type Variation,
  type Bucket,
  createDefaultExperiment,
  createDefaultRule,
  createDefaultFilter,
  createDefaultVariation,
  createDefaultBucket,
  getFilterTypeKey,
} from "../types/experiment";

export interface ExperimentStore {
  experiment: Experiment;
  errors: Record<string, string>;
  setExperiment: (experiment: Experiment) => void;
  setErrors: (errors: Record<string, string>) => void;
  reinitialize: () => void;

  updateField: <K extends keyof Experiment>(key: K, value: Experiment[K]) => void;

  addRule: () => void;
  removeRule: (index: number) => void;
  updateRule: (index: number, rule: Rule) => void;
  moveRule: (fromIndex: number, toIndex: number) => void;

  addFilter: (ruleIndex: number, filterType: string) => void;
  removeFilter: (ruleIndex: number, filterIndex: number) => void;
  updateFilter: (ruleIndex: number, filterIndex: number, filter: Filter) => void;
  moveFilter: (ruleIndex: number, fromIndex: number, toIndex: number) => void;

  addVariation: () => void;
  removeVariation: (index: number) => void;
  updateVariation: (index: number, variation: Variation) => void;
  moveVariation: (fromIndex: number, toIndex: number) => void;

  addBucket: (variationIndex: number) => void;
  removeBucket: (variationIndex: number, bucketIndex: number) => void;
  updateBucket: (variationIndex: number, bucketIndex: number, bucket: Bucket) => void;
  moveBucket: (variationIndex: number, fromIndex: number, toIndex: number) => void;

  setRawJson: (json: string) => boolean;
}

export const useExperimentStore = create<ExperimentStore>((set, get) => ({
  experiment: createDefaultExperiment(),
  errors: {},

  setExperiment: (experiment) => set({ experiment }),
  setErrors: (errors) => set({ errors }),
  reinitialize: () => set({ experiment: createDefaultExperiment(), errors: {} }),

  updateField: (key, value) =>
    set((state) => ({
      experiment: { ...state.experiment, [key]: value },
    })),

  addRule: () =>
    set((state) => ({
      experiment: {
        ...state.experiment,
        rules: [...state.experiment.rules, createDefaultRule()],
      },
    })),

  removeRule: (index) =>
    set((state) => ({
      experiment: {
        ...state.experiment,
        rules: state.experiment.rules.filter((_, i) => i !== index),
      },
    })),

  updateRule: (index, rule) =>
    set((state) => {
      const rules = [...state.experiment.rules];
      rules[index] = rule;
      return { experiment: { ...state.experiment, rules } };
    }),

  moveRule: (fromIndex, toIndex) =>
    set((state) => {
      const rules = [...state.experiment.rules];
      const [moved] = rules.splice(fromIndex, 1);
      rules.splice(toIndex, 0, moved);
      return { experiment: { ...state.experiment, rules } };
    }),

  addFilter: (ruleIndex, filterType) =>
    set((state) => {
      const rules = [...state.experiment.rules];
      const rule = { ...rules[ruleIndex] };
      rule.filters = [...rule.filters, createDefaultFilter(filterType)];
      rules[ruleIndex] = rule;
      return { experiment: { ...state.experiment, rules } };
    }),

  removeFilter: (ruleIndex, filterIndex) =>
    set((state) => {
      const rules = [...state.experiment.rules];
      const rule = { ...rules[ruleIndex] };
      rule.filters = rule.filters.filter((_, i) => i !== filterIndex);
      rules[ruleIndex] = rule;
      return { experiment: { ...state.experiment, rules } };
    }),

  updateFilter: (ruleIndex, filterIndex, filter) =>
    set((state) => {
      const rules = [...state.experiment.rules];
      const rule = { ...rules[ruleIndex] };
      rule.filters = [...rule.filters];
      rule.filters[filterIndex] = filter;
      rules[ruleIndex] = rule;
      return { experiment: { ...state.experiment, rules } };
    }),

  moveFilter: (ruleIndex, fromIndex, toIndex) =>
    set((state) => {
      const rules = [...state.experiment.rules];
      const rule = { ...rules[ruleIndex] };
      const filters = [...rule.filters];
      const [moved] = filters.splice(fromIndex, 1);
      filters.splice(toIndex, 0, moved);
      rule.filters = filters;
      rules[ruleIndex] = rule;
      return { experiment: { ...state.experiment, rules } };
    }),

  addVariation: () =>
    set((state) => ({
      experiment: {
        ...state.experiment,
        variations: [...state.experiment.variations, createDefaultVariation()],
      },
    })),

  removeVariation: (index) =>
    set((state) => ({
      experiment: {
        ...state.experiment,
        variations: state.experiment.variations.filter((_, i) => i !== index),
      },
    })),

  updateVariation: (index, variation) =>
    set((state) => {
      const variations = [...state.experiment.variations];
      variations[index] = variation;
      return { experiment: { ...state.experiment, variations } };
    }),

  moveVariation: (fromIndex, toIndex) =>
    set((state) => {
      const variations = [...state.experiment.variations];
      const [moved] = variations.splice(fromIndex, 1);
      variations.splice(toIndex, 0, moved);
      return { experiment: { ...state.experiment, variations } };
    }),

  addBucket: (variationIndex) =>
    set((state) => {
      const variations = [...state.experiment.variations];
      const variation = { ...variations[variationIndex] };
      variation.buckets = [...variation.buckets, createDefaultBucket()];
      variations[variationIndex] = variation;
      return { experiment: { ...state.experiment, variations } };
    }),

  removeBucket: (variationIndex, bucketIndex) =>
    set((state) => {
      const variations = [...state.experiment.variations];
      const variation = { ...variations[variationIndex] };
      variation.buckets = variation.buckets.filter((_, i) => i !== bucketIndex);
      variations[variationIndex] = variation;
      return { experiment: { ...state.experiment, variations } };
    }),

  updateBucket: (variationIndex, bucketIndex, bucket) =>
    set((state) => {
      const variations = [...state.experiment.variations];
      const variation = { ...variations[variationIndex] };
      variation.buckets = [...variation.buckets];
      variation.buckets[bucketIndex] = bucket;
      variations[variationIndex] = variation;
      return { experiment: { ...state.experiment, variations } };
    }),

  moveBucket: (variationIndex, fromIndex, toIndex) =>
    set((state) => {
      const variations = [...state.experiment.variations];
      const variation = { ...variations[variationIndex] };
      const buckets = [...variation.buckets];
      const [moved] = buckets.splice(fromIndex, 1);
      buckets.splice(toIndex, 0, moved);
      variation.buckets = buckets;
      variations[variationIndex] = variation;
      return { experiment: { ...state.experiment, variations } };
    }),

  setRawJson: (json) => {
    try {
      const parsed = JSON.parse(json);
      set({ experiment: parsed, errors: {} });
      return true;
    } catch {
      return false;
    }
  },
}));
